import { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@mlm/ui';
import { Download } from 'lucide-react';

/**
 * A QR code with a "Download PNG" action. Renders the code to a <canvas>
 * (QRCodeCanvas) so the bitmap can be exported via `toDataURL` — used for both
 * the leads (referral) link and the product replicated link so members can save
 * and reshare the image. The white padded frame keeps the quiet zone scanners
 * need even when dropped onto a colored background.
 */
export function DownloadableQr({
  value,
  size = 192,
  fileName = 'qr-code',
  caption,
}: {
  value: string;
  size?: number;
  /** Download file name (without extension). */
  fileName?: string;
  caption?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);

  function download() {
    const canvas = wrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <div ref={wrapRef} className="inline-block rounded-md bg-white p-4">
        <QRCodeCanvas value={value} size={size} level="M" marginSize={2} />
      </div>
      {caption ? <p className="text-center text-xs text-muted-foreground">{caption}</p> : null}
      <Button onClick={download} variant="outline" size="sm" disabled={!value}>
        <Download className="mr-1 h-4 w-4" />
        Download PNG
      </Button>
    </div>
  );
}
